import type {
    MetricChartSpec,
    MetricLineGradient,
    MetricPointMarker,
    MetricSeriesPoint,
    MetricSeriesSpec,
} from '@/components/charts/generic';
import { graphSettings, OptoColors } from '@/config/settings';
import type { BaseChartDataPoint, ThresholdRange } from '@/domain/behavior';
import { formatDateShortLocalized } from '@/utils/date';
import { formatNumberByLocale, type Locale } from '@/utils/localeFormatters';

/**
 * What the Behavior charts share, as MetricChart spec parts: softly curved lines without
 * markers at rest, threshold-coloured swim speed and tilt, context bands (temperature, depth,
 * oxygen) on the right axis, and the day's measurement count in the tooltip footer.
 */

export const BEHAVIOR_LINE_TENSION = 0.25;

/** The pale band blue every context band fades from, lifted on the dark card. */
export const BEHAVIOR_BAND_COLOR = graphSettings.graphColors.band;
export const BEHAVIOR_GREEN = OptoColors.OceanGreen;
export const BEHAVIOR_RIVERSIDE = OptoColors.Riverside;
export const BEHAVIOR_ORANGE = OptoColors.Orange;

/** No markers on the line; the hovered point is shown. */
export const BEHAVIOR_LINE_MARKER: MetricPointMarker = {
    radius: 0,
    hoverRadius: 4,
    borderWidth: 0,
};

/** Swim speed is good between the two thresholds, blending over 0.1 body lengths a second. */
const SWIM_SPEED_TRANSITION = 0.1;
/** Tilt is good below the optimum, blending over half a degree. */
const TILT_TRANSITION = 0.5;

/** A day key that carries an hour: the 24-hour view. */
function isHourKey(day: string): boolean {
    return day.includes(' ') && day.includes(':');
}

/**
 * The x axis of a Behavior chart: one key per point, read as days or, in the 24-hour view, as
 * hours. A day is titled in the tooltip with its localised date (`Sep 12, 2026`).
 */
export function behaviorXAxis(
    points: BaseChartDataPoint[],
    locale: Locale
): Pick<MetricChartSpec<'line'>, 'days' | 'xAxisKind' | 'tooltipTitle'> {
    const days = points.map((point) => point.day);
    if (days.some(isHourKey)) return { days, xAxisKind: 'hour' };
    return { days, tooltipTitle: (day) => formatDateShortLocalized(day, locale) };
}

/** One series' points, a missing or NaN value a gap. */
export function behaviorPoints<TPoint extends BaseChartDataPoint>(
    points: TPoint[],
    value: (point: TPoint) => number | null | undefined
): MetricSeriesPoint[] {
    return points.map((point) => {
        const raw = value(point);
        return { day: point.day, value: raw == null || Number.isNaN(raw) ? null : raw };
    });
}

/** Swim speed's colours: good between the threshold's min and max. */
export function swimSpeedGradient(threshold: ThresholdRange): MetricLineGradient {
    return {
        kind: 'between',
        lower: threshold.min,
        upper: threshold.max,
        transition: SWIM_SPEED_TRANSITION,
    };
}

/** Tilt's colours: good below the threshold's optimum. */
export function tiltGradient(threshold: ThresholdRange): MetricLineGradient {
    return { kind: 'above', threshold: threshold.optimal, transition: TILT_TRANSITION };
}

/**
 * A context band on the right axis, faded from the band blue. `color` is what its legend
 * entry and tooltip swatch show; it defaults to the band blue too.
 */
export function behaviorBand(
    series: Pick<MetricSeriesSpec, 'key' | 'label' | 'data'> & { color?: string }
): MetricSeriesSpec {
    return {
        key: series.key,
        label: series.label,
        color: series.color ?? BEHAVIOR_BAND_COLOR,
        data: series.data,
        axis: 'right',
        fill: true,
        fillGradient: { topOpacity: 1, bottomOpacity: 0.05, color: BEHAVIOR_BAND_COLOR },
        hideLine: true,
        tension: BEHAVIOR_LINE_TENSION,
    };
}

/**
 * `Measurements: 311` under the hovered point, or `label(point)` in place of "Measurements"
 * (the respiration charts name an uncertain count). Nothing for a point without a count.
 */
export function measurementsFooter<TPoint extends BaseChartDataPoint>(
    points: TPoint[],
    count: (point: TPoint) => number | null | undefined,
    label: (point: TPoint) => string,
    locale: Locale
): (index: number) => string[] {
    return (index) => {
        const point = points[index];
        const value = point ? count(point) : undefined;
        if (!point || value == null) return [];
        return [`${label(point)}: ${formatNumberByLocale(value, locale, 0)}`];
    };
}
